// src/components/intake/YoureInPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { Button, Pass } from "../ui";
import {
  continueWelcome,
  getIntake,
} from "../../shared/_services/intake.services";
import { CHOOSE_PLAN_PATH } from "../../shared/_helpers/chatGate";
import { onboardingRouteFor } from "../../shared/_helpers/onboardingRoutes";
import { BRAND, GENERIC_ERROR } from "../onboarding/onboardingCopy";
import { isWrongStage, useStageNavigation } from "../onboarding/onboardingNav";
import {
  YOURE_IN_FIRST_CHARGE,
  YOURE_IN_FIRST_CHARGE_VALUE,
  YOURE_IN_H1,
  YOURE_IN_INTRO,
  YOURE_IN_NEXT_HEADING,
  YOURE_IN_NEXT_PLAN,
  YOURE_IN_NEXT_PLAN_BODY,
  YOURE_IN_NEXT_QUESTIONS,
  YOURE_IN_NEXT_QUESTIONS_BODY,
  YOURE_IN_NEXT_THREAD,
  YOURE_IN_NEXT_THREAD_BODY,
  YOURE_IN_PASS_BIG,
  YOURE_IN_PASS_KIND,
  YOURE_IN_SETUP_FEE,
  YOURE_IN_SETUP_FEE_VALUE,
  YOURE_IN_SKIP,
  YOURE_IN_START,
} from "./intakeCopy";

// /welcome — You're in (spec § Screens). Mounted inside
// <OnboardingGate stageKey="trial_start">: a client ahead of trial_start is
// redirected to their own page, a client past it sees this page read-only.
// Both buttons send POST /onboarding/welcome/continue (decision 10); read-only
// they only navigate (decision 18). Start opens the wizard at the server's
// nextPageKey; Skip goes to choose a plan (variant A, decision 29).
export default function YoureInPage({ readOnly = false, stage }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = useStageNavigation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toWizard = async () => {
    const res = await getIntake();
    const next = res.data.nextPageKey;
    navigate(next ? `/intake/${next}` : onboardingRouteFor("intake"));
  };

  const press = (start) => async () => {
    setBusy(true);
    setError(null);
    try {
      if (!readOnly) await continueWelcome();
      if (start) await toWizard();
      else navigate(CHOOSE_PLAN_PATH);
    } catch (err) {
      if (isWrongStage(err)) {
        go(err.response.data);
        return;
      }
      setError(err?.response?.data?.message || GENERIC_ERROR);
      setBusy(false);
    }
  };

  const next = [
    [
      YOURE_IN_NEXT_QUESTIONS(stage?.intakePageCount),
      YOURE_IN_NEXT_QUESTIONS_BODY,
    ],
    [YOURE_IN_NEXT_PLAN, YOURE_IN_NEXT_PLAN_BODY],
    [YOURE_IN_NEXT_THREAD, YOURE_IN_NEXT_THREAD_BODY],
  ];

  return (
    <div className="stars-dark tw-min-h-screen tw-bg-bg tw-font-sans tw-text-text">
      <main className="tw-mx-auto tw-flex tw-max-w-3xl tw-flex-col tw-items-center tw-gap-6 tw-px-4 tw-py-16 tw-text-center">
        <p className="tw-m-0 tw-text-md tw-font-bold">{BRAND}</p>
        <div>
          <h1 className="tw-m-0 tw-text-2xl tw-font-bold">
            {YOURE_IN_H1(user?.firstName)}
          </h1>
          <p className="tw-mx-auto tw-mb-0 tw-mt-3 tw-max-w-xl tw-text-md tw-text-text-muted">
            {YOURE_IN_INTRO}
          </p>
        </div>
        <Pass
          compact
          kind={YOURE_IN_PASS_KIND}
          className="tw-w-full tw-max-w-md tw-text-left"
          stub={
            <dl className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-8 tw-gap-y-4">
              {[
                [YOURE_IN_SETUP_FEE, YOURE_IN_SETUP_FEE_VALUE],
                [YOURE_IN_FIRST_CHARGE, YOURE_IN_FIRST_CHARGE_VALUE],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-text-muted">
                    {label}
                  </dt>
                  <dd className="tw-m-0 tw-text-sm tw-font-semibold">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          }
        >
          <p className="tw-m-0 tw-text-2xl tw-font-bold">{YOURE_IN_PASS_BIG}</p>
        </Pass>
        <section
          aria-labelledby="youre-in-next"
          className="tw-w-full tw-max-w-xl tw-text-left"
        >
          <h2 id="youre-in-next" className="tw-sr-only">
            {YOURE_IN_NEXT_HEADING}
          </h2>
          <ol className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0">
            {next.map(([title, body]) => (
              <li
                key={title}
                className="tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-px-6 tw-py-4"
              >
                <b className="tw-block tw-text-sm">{title}</b>
                <span className="tw-text-sm tw-text-text-muted">{body}</span>
              </li>
            ))}
          </ol>
        </section>
        <div className="tw-grid tw-justify-items-center tw-gap-6">
          <Button loading={busy} onClick={press(true)}>
            {YOURE_IN_START}
          </Button>
          <Button variant="text" loading={busy} onClick={press(false)}>
            {YOURE_IN_SKIP}
          </Button>
          {error && (
            <p role="alert" className="tw-m-0 tw-text-sm tw-text-danger">
              {error}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
